// Our own SVG charts (owner 2026-10-31: the Google chart library is gone; it was 280 KB from gstatic, loaded for one
// pie). A donut with its legend, vertical bars, horizontal bars and a two-part meter, enough for the Dashboard's
// Panel and Details tabs. Each chart measures its box (ResizeObserver) and draws in pixels, so the text keeps its
// size at every width. Colours: eight fixed slots in an order that stays apart for colour-blind readers (checked with
// the dataviz palette validator on the window's grey box); a ninth series folds into the grey "other".
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './svgCharts.css';

export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'] as const;
export const OTHER_COLOR = '#8a8a8a';
/** How many series get a colour of their own; the rest fold into "other" (the eight slots are the validated set). */
export const MAX_SERIES = 7;

export interface Slice {
  key: string;
  label: string;
  value: number;
  color: string;
}

/** The width (and height) of an element, followed while it lives. */
function useBox<T extends HTMLElement>(): [React.RefObject<T | null>, { width: number; height: number }] {
  const ref = useRef<T>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setBox((b) => (b.width === w && b.height === h ? b : { width: w, height: h }));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, box];
}

const fmt = (n: number): string => n.toLocaleString();
const pct = (part: number, total: number): string => (total ? `${((part / total) * 100).toFixed(1)}%` : '0%');

/** Folds the series beyond MAX_SERIES into one "other" slice and gives each a colour in the fixed order. */
export function toSlices(items: Array<{ key: string; label: string; value: number }>, otherLabel: string): Slice[] {
  const sorted = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, sorted.length > MAX_SERIES + 1 ? MAX_SERIES : MAX_SERIES + 1);
  const rest = sorted.slice(head.length);
  const out: Slice[] = head.map((i, k) => ({ ...i, color: SERIES[k] ?? OTHER_COLOR }));
  if (rest.length)
    out.push({ key: 'other', label: otherLabel, value: rest.reduce((s, i) => s + i.value, 0), color: OTHER_COLOR });
  return out;
}

function arcPath(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number) => `${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${p(r1, a0)} A ${r1} ${r1} 0 ${large} 1 ${p(r1, a1)} L ${p(r0, a1)} A ${r0} ${r0} 0 ${large} 0 ${p(r0, a0)} Z`;
}

/**
 * A donut with the total in its hole and a legend beside it (under it when the box is narrow). The slices are
 * separated by a 2 px gap of the surface; a slice narrower than that is drawn without one. `data-e2e-chart="pie"`
 * on the box and one `<path data-slice>` per slice for the tests.
 */
export function Donut({
  slices,
  total,
  totalLabel,
  id,
  className,
}: {
  slices: Slice[];
  total: number;
  totalLabel: string;
  id?: string;
  className?: string;
}) {
  const [ref, box] = useBox<HTMLDivElement>();
  const narrow = box.width > 0 && box.width < 360;
  // beside the legend: half the width, within the height left; under it (narrow): a fixed, readable size
  const size = narrow
    ? Math.max(150, Math.min(box.width, 200))
    : Math.max(110, Math.min(box.width * 0.5, box.height > 0 ? box.height : 260, 260));
  const cx = size / 2;
  const r1 = size / 2 - 2;
  const r0 = r1 * 0.58;
  const sum = slices.reduce((s, x) => s + x.value, 0);
  const gap = sum > 0 ? 2 / r1 : 0; // 2 px of surface between slices, in radians at the outer rim
  const paths = slices.reduce<{ angle: number; out: Array<Slice & { d: string }> }>(
    (acc, s) => {
      const span = sum ? (s.value / sum) * Math.PI * 2 : 0;
      const a1 = acc.angle + span;
      const g = span > gap * 2 && slices.length > 1 ? gap / 2 : 0;
      acc.out.push({ ...s, d: arcPath(cx, cx, r0, r1, acc.angle + g, a1 - g) });
      return { angle: a1, out: acc.out };
    },
    { angle: -Math.PI / 2, out: [] },
  ).out;
  return (
    <div
      ref={ref}
      id={id}
      className={`viz_donut${narrow ? ' viz_narrow' : ''}${className ? ` ${className}` : ''}`}
      data-e2e-chart="pie"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${totalLabel}: ${fmt(total)}`}
      >
        {paths.map((p) => (
          <path key={p.key} d={p.d} fill={p.color} data-slice={p.key}>
            <title>{`${p.label}: ${fmt(p.value)} (${pct(p.value, sum)})`}</title>
          </path>
        ))}
        <text
          x={cx}
          y={size < 150 ? cx + 8 : cx - 4}
          textAnchor="middle"
          className={`viz_donut_total${size < 150 ? ' viz_small' : ''}`}
        >
          {fmt(total)}
        </text>
        {size >= 150 ? (
          <text x={cx} y={cx + 16} textAnchor="middle" className="viz_donut_label">
            {totalLabel}
          </text>
        ) : null}
      </svg>
      <ul className="viz_legend">
        {slices.map((s) => (
          <li key={s.key} data-legend={s.key}>
            <i style={{ background: s.color }} />
            <span className="viz_legend_name">{s.label}</span>
            <span className="viz_legend_value">{fmt(s.value)}</span>
            <span className="viz_legend_pct">{pct(s.value, sum)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface Bar {
  key: string;
  label: string;
  value: number;
  /** The tooltip (default: "label: value"). */
  title?: string;
}

/**
 * Vertical bars on a light grid: one colour (one series), 4 px rounded tops, a value over the tallest bars, every
 * `labelEvery`-th label under the axis. Hover shows the tooltip of the bar.
 */
export function Bars({
  bars,
  height = 160,
  color = SERIES[0],
  labelEvery = 1,
  className,
}: {
  bars: Bar[];
  height?: number;
  color?: string;
  labelEvery?: number;
  className?: string;
}) {
  const [ref, box] = useBox<HTMLDivElement>();
  const width = box.width || 300;
  const top = 18;
  const bottom = 22;
  const left = 4;
  const plotH = height - top - bottom;
  const max = Math.max(1, ...bars.map((b) => b.value));
  const n = Math.max(1, bars.length);
  const slot = (width - left * 2) / n;
  const barW = Math.max(2, Math.min(slot - 2, 44));
  const r = Math.min(4, barW / 2);
  const ticks = [max, max / 2].map((v) => Math.round(v));
  const labelWidth = Math.max(26, ...bars.map((b) => b.label.length * 6.5 + 8));
  const labelThin = Math.max(labelEvery, Math.ceil(labelWidth / slot));
  const showValue = (v: number) => v > 0 && v >= max * 0.6 && barW >= 14;
  return (
    <div ref={ref} className={`viz_bars${className ? ` ${className}` : ''}`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img">
        {ticks.map((v) => {
          const y = top + plotH - (v / max) * plotH;
          return <line key={v} x1={left} x2={width - left} y1={y} y2={y} className="viz_grid" />;
        })}
        <line x1={left} x2={width - left} y1={top + plotH} y2={top + plotH} className="viz_axis" />
        {bars.map((b, i) => {
          const h = (b.value / max) * plotH;
          const x = left + i * slot + (slot - barW) / 2;
          const y = top + plotH - h;
          const rr = Math.min(r, h);
          const d =
            h > 0
              ? `M ${x} ${top + plotH} V ${y + rr} Q ${x} ${y} ${x + rr} ${y} H ${x + barW - rr} Q ${x + barW} ${y} ${x + barW} ${y + rr} V ${top + plotH} Z`
              : '';
          return (
            <g key={b.key} className="viz_bar" data-bar={b.key} data-value={b.value}>
              <rect x={left + i * slot} y={top} width={slot} height={plotH} fill="transparent" />
              {d ? <path d={d} fill={color} /> : null}
              {showValue(b.value) ? (
                <text x={x + barW / 2} y={y - 4} textAnchor="middle" className="viz_value">
                  {fmt(b.value)}
                </text>
              ) : null}
              {i % labelThin === 0 ? (
                <text x={left + i * slot + slot / 2} y={height - 6} textAnchor="middle" className="viz_tick">
                  {b.label}
                </text>
              ) : null}
              <title>{b.title ?? `${b.label}: ${fmt(b.value)}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Horizontal bars with the label on the left and the value on the right; the bar is a share of the longest. */
export function HBars({
  bars,
  color = SERIES[0],
  className,
}: {
  bars: Array<Bar & { color?: string }>;
  color?: string;
  className?: string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.value));
  return (
    <ul className={`viz_hbars${className ? ` ${className}` : ''}`}>
      {bars.map((b) => (
        <li key={b.key} data-bar={b.key} title={b.title ?? `${b.label}: ${fmt(b.value)}`}>
          <span className="viz_hbar_label">{b.label}</span>
          <span className="viz_hbar_track">
            <i style={{ width: `${(b.value / max) * 100}%`, background: b.color ?? color }} />
          </span>
          <span className="viz_hbar_value">{fmt(b.value)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Two parts of one whole (new and renewed deals): one track, two fills, the words with counts and shares under it. */
export function Meter({
  a,
  b,
  className,
}: {
  a: { label: string; value: number };
  b: { label: string; value: number };
  className?: string;
}) {
  const total = a.value + b.value;
  const left = total ? (a.value / total) * 100 : 0;
  return (
    <div className={`viz_meter${className ? ` ${className}` : ''}`}>
      <div
        className="viz_meter_track"
        role="img"
        aria-label={`${a.label} ${pct(a.value, total)}, ${b.label} ${pct(b.value, total)}`}
      >
        <i style={{ width: `${left}%`, background: SERIES[0] }} />
        <i style={{ width: `${100 - left}%`, background: SERIES[1] }} />
      </div>
      <div className="viz_meter_words">
        <span>
          <b style={{ background: SERIES[0] }} />
          {a.label}: {fmt(a.value)} ({pct(a.value, total)})
        </span>
        <span>
          <b style={{ background: SERIES[1] }} />
          {b.label}: {fmt(b.value)} ({pct(b.value, total)})
        </span>
      </div>
    </div>
  );
}

/** A number with its name under it (a stat tile); `note` is a smaller third line. */
export function Tile({ value, label, note }: { value: ReactNode; label: string; note?: ReactNode }) {
  return (
    <div className="viz_tile">
      <div className="viz_tile_value">{value}</div>
      <div className="viz_tile_label">{label}</div>
      {note ? <div className="viz_tile_note">{note}</div> : null}
    </div>
  );
}

/** Runs `fn` once, the first time `on` is true (the Details tab loads when it is first shown). */
export function useOnce(on: boolean, fn: () => void): void {
  const done = useRef(false);
  useEffect(() => {
    if (!on || done.current) return;
    done.current = true;
    fn();
  }, [on, fn]);
}
